import { CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import React, { useState } from 'react'
import CartLinesTotal from './components/CartLinesTotal'
import Button from '@mui/material/Button'
import { useTranslation } from 'react-i18next'
import WholesaleCartSummary from '@components/WholesaleCartSummary'
import WholesaleCartLinesContainer from '@components/WholesaleCartLines/WholesaleCartLinesContainer'
import OrderNoteForm from '@components/Forms/OrderNoteForm'
import { OrderNoteFragment } from '@graphql/fragments/__generated__/OrderNote.generated'
import FormattedDate from '@components/FormattedDate'

interface CategoryCartLine {
  pk: number
  pcs: number
  qty: number
  price: number
  weight: number
  category: string
  sortOrder: number
  currency: string
}

interface PageProps {
  cartQueryResult: CartQueryResult
  orderNotes: OrderNoteFragment[]

  cartClear(categoryId?: string): Promise<void>
  onSaveAsDraft(): Promise<void>
  onCreateOrderNote(values: { orderId: number; content: string; replyToMe: boolean }): Promise<void>
  onDeleteOrder(): void
}

const Page: React.FC<PageProps> = ({
  cartQueryResult,
  orderNotes,
  cartClear,
  onSaveAsDraft,
  onCreateOrderNote,
  onDeleteOrder,
}) => {
  const { t } = useTranslation()
  const [isNoteFormShow, setIsNoteFormShow] = useState(false)
  const onAddNote = () => {
    setIsNoteFormShow(true)
  }
  const categoriesWithLines: {
    [key: number]: CategoryCartLine[]
  } = cartQueryResult.data?.cart?.linesByCategory ? cartQueryResult.data.cart.linesByCategory : {}
  const orderId = cartQueryResult.data?.cart?.orderId
  return (
    <div className='p-0 sm:p-24 flex flex-col md:flex-row justify-center bg-gray-50' style={{ minHeight: 'inherit' }}>
      <div className='w-full md:w-700 sm:mr-24'>
        <WholesaleCartLinesContainer />
      </div>
      <div className='w-full md:w-400 mt-20 md:mt-0 flex flex-col'>
        {!!Object.keys(categoriesWithLines).length && (
          <WholesaleCartSummary categoriesWithLines={categoriesWithLines} />
        )}
        <CartLinesTotal
          cartQueryResult={cartQueryResult}
          loading={cartQueryResult.loading}
          onSaveAsDraft={onSaveAsDraft}
          onAddNote={onAddNote}
          onDeleteOrder={onDeleteOrder}
        />
        {orderId && isNoteFormShow && (
          <OrderNoteForm
            onSubmit={async values => {
              setIsNoteFormShow(false)
              await onCreateOrderNote({ orderId, ...values })
            }}
            onCancel={() => setIsNoteFormShow(false)}
          />
        )}
        {!orderId && cartQueryResult.data?.cart?.count > 0 && (
          <div className='mt-10 bg-white shadow-md p-10'>
            <p className='text-bold text-base'>{t('title.clearTheBagOfGoods', 'Clear the bag of goods')}</p>
            <p className='pb-15 text-gray'>
              {t(
                'description.allProductsWillBeDeletedFromYourShoppingBag',
                'All products will be deleted from your shopping bag after click the button below'
              )}
            </p>
            <Button variant='outlined' color='warning' size='medium' onClick={() => cartClear()} fullWidth>
              {t('button.deleteAllProducts', 'Delete all products')}
            </Button>
          </div>
        )}
        {!!orderNotes.length && (
          <div className='mt-10 bg-white shadow-md p-10'>
            <p className='text-bold text-base'>{t('title.notes', 'Notes')}</p>
            {orderNotes.map(node => (
              <div key={node.id} className='pb-10 text-gray text-sm'>
                <div>
                  {node.creator} - <FormattedDate value={node.date} variant='long' />
                </div>
                <pre style={{ fontFamily: 'inherit', margin: 0 }}>{node.content}</pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Page
